import { AccessIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/sales/AccessIcon";
import { AffiliatesIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/sales/AffiliatesIcon";
import { BagIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/sales/BagIcon";
import { CellphoneIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/sales/CellphoneIcon";
import { CommissioningIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/sales/CommissioningIcon";
import { CuponsIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/sales/CuponsIcon";
import { OffersIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/sales/OffersIcon";
import { OrdersIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/sales/OrdersIcon";
import { SplitsIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/sales/SplitsIcon";
import { StatmentIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/sales/StatmentIcon";
import { TransactionsIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/sales/TransactionsIcon";
import { WithdrawIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/sales/WithdrawIcon";
import {
  IOption,
  IOptionsHook,
  ISubSection,
} from "pages/[tenant]/console/types";

import SmartInstallmentsStagesIcon from "shared/assets/icons/SmartInstallmentsStagesIcon";
import { useAuthContext } from "shared/utils/hooks/useAuthContext";
import { useFeature } from "shared/utils/hooks/useFeature";

import HelpDeskIcon from "@Console/assets/icons/sidebarSection/sales/HelpDeskIcon";
import { permissionsAccessPageTransactions } from "@Console/utils/permissionsAreasAdminConsole";

export const useSalesOptions = ({
  amountOfCancellationTicketsOpen,
}): IOptionsHook => {
  const orderBumpIsEnabled = useFeature("f_order_bump");
  const isSmartInstallmentEnabled = useFeature("f_smart_installments");
  const isConsoleHelpDeskEnabled = useFeature("f_call_center");

  const { loggedUser, rolesUser } = useAuthContext();
  const isCoProducer = loggedUser?.has_recipient;

  const hasAccessToHelpDeskModerateRefund = rolesUser?.includes(
    "moderar-tickets-de-reembolso",
  );
  const hasAccessToHelpDeskModerateRecurrence = rolesUser?.includes(
    "moderar-tickets-de-cancelamento-de-recorrencia",
  );

  const shouldShowHelpDeskSection =
    (hasAccessToHelpDeskModerateRefund ||
      hasAccessToHelpDeskModerateRecurrence) &&
    isConsoleHelpDeskEnabled;

  const hasPermissionStatement =
    rolesUser?.includes("visualizar-o-extrato") || isCoProducer;

  const hasPermissionCoupons = rolesUser?.includes("visualizar-cupons");

  const hasPermissionTransaction =
    permissionsAccessPageTransactions?.some((permission) =>
      rolesUser?.includes(permission),
    ) || isCoProducer;

  const hasPermissionAccess = rolesUser?.includes(
    "visualizar-acessos-vendidos",
  );
  const hasPermissionOrderBump = rolesUser?.includes("visualizar-order-bumps");
  const userIsMaster = loggedUser?.id === 2;
  const userIsSuperadmin =
    loggedUser?.console_role?.slug === "superadmin" || loggedUser?.superadmin;
  const hasPermissionToAppNotification = userIsSuperadmin || userIsMaster;

  const hasPermissionAffiliates = rolesUser?.includes(
    "visualizar-configuracoes-do-programa",
  );

  const sectionLabel = "Vendas";
  const sales: IOption[] = [
    {
      id: 1,
      label: "Ofertas",
      path: "/console/offers",
      icon: <OffersIcon />,
      shouldHide: true,
    },
    {
      id: 2,
      label: "Pedidos",
      path: "/console/orders",
      icon: <OrdersIcon />,
      shouldHide: true,
    },
    {
      id: 3,
      label: "Transações",
      description:
        "Acompanhe as transações realizadas por suas vendas no checkout da Ensinio Pay.",
      path: "/console/transactions",
      icon: <TransactionsIcon />,
      shouldHide: !hasPermissionTransaction,
    },
    {
      id: 4,
      label: "Acessos vendidos",
      description:
        "Monitore os acessos e a renovação das assinaturas na sua plataforma.",
      path: "/console/access",
      icon: <AccessIcon />,
      shouldHide: !hasPermissionAccess,
    },
    {
      id: 9,
      label: "Parcelamento inteligente",
      description:
        "Acompanhe todas as compras recuperadas com o parcelamento inteligente!",
      path: "/console/smart-installment",
      icon: (
        <SmartInstallmentsStagesIcon
          paidInstallments={12}
          totalInstallments={12}
          rectFill="currentColor"
        />
      ),
      shouldHide: !isSmartInstallmentEnabled,
    },
    {
      id: 5,
      label: "Order Bump",
      description: "",
      path: "/console/order_bump",
      icon: <BagIcon />,
      shouldHide: !orderBumpIsEnabled || !hasPermissionOrderBump,
    },
    {
      id: 6,
      label: "Cupons",
      description:
        "Potencialize suas vendas com nossa página de cupons. Acompanhe de perto o uso dos cupons e ajuste suas estratégias para alcançar o máximo retorno sobre investimento.",
      path: "/console/coupons",
      icon: <CuponsIcon />,
      shouldHide: !hasPermissionCoupons,
    },
    {
      id: 7,
      label: "Split de vendas",
      path: "/console/split",
      icon: <SplitsIcon />,
      shouldHide: true,
    },
    {
      id: 8,
      label: "Extrato e saques",
      description:
        "Acompanhe o repasse das suas receitas e gerencie suas solicitações de saques.",
      path: "/console/statement",
      icon: <StatmentIcon />,
      shouldHide: !hasPermissionStatement,
    },
  ];

  const salesSubSection: ISubSection[] = [
    {
      label: "Central",
      options: [
        {
          id: 31,
          label: "Atendimento",
          description:
            "Acompanhe, responda e revise todos as solicitações de reembolso e recorrência da sua plataforma em um único lugar.",
          path: "/console/help-desk",
          icon: <HelpDeskIcon />,
          notificationNumber:
            amountOfCancellationTicketsOpen > 0
              ? amountOfCancellationTicketsOpen
              : null,
          shouldHide: !shouldShowHelpDeskSection,
        },
      ],
    },
    hasPermissionAffiliates
      ? {
          label: "Afiliados",
          options: [
            {
              id: 19,
              label: "Programa de afiliados",
              description:
                "Crie, gerencie e estabeleça parcerias sólidas com seus afiliados com uma relação ganha-ganha.",
              path: "/console/commissioning",
              icon: <CommissioningIcon />,
            },
            {
              id: 20,
              label: "Meus afiliados",
              description:
                "Acompanhe as atividades e vendas dos seus afiliados.",
              path: "/console/affiliates",
              icon: <AffiliatesIcon />,
            },

            {
              id: 21,
              label: "Saques dos afiliados",
              description:
                "Acompanhe as solicitações de saques dos seus afiliados.",
              path: "/console/withdraw",
              icon: <WithdrawIcon />,
            },
          ],
        }
      : ({} as any),

    {
      label: "Notificações",
      options: [
        {
          id: 22,
          label: "Aplicativo de notificação",
          path: "/console/app/notification",
          icon: <CellphoneIcon />,
          shouldHide: !hasPermissionToAppNotification,
        },
      ],
    },
  ];

  return {
    options: sales,
    label: sectionLabel,
    subSections: salesSubSection,
  };
};
